import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { ViewProvider } from './context/ViewContext';
import { OrganismeProvider } from './context/OrganismeContext';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import MaintenanceGate from './components/MaintenanceGate';
import PreprodBanner from './components/PreprodBanner';
import NewHome from './pages/NewHome';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard'; 
import MesAdhesions from './pages/MesAdhesions';
import EditProfile from './pages/EditProfile';
import Security from './pages/Security';
import Adhesion from './pages/Adhesion';
import GroupementSAR from './pages/GroupementSAR';
import GroupementAMAIR from './pages/GroupementAMAIR';
import GroupementADAR from './pages/GroupementADAR';
import GroupementCOOPEMIEL from './pages/GroupementCOOPEMIEL';
import AdhesionSAR from './pages/AdhesionSAR';
import AdhesionAMAIR from './pages/AdhesionAMAIR';
import AdhesionADAR from './pages/AdhesionADAR';
import AdhesionCOOPEMIEL from './pages/AdhesionCOOPEMIEL';
import AdhesionDetail from './pages/AdhesionDetail';
import ReglementAdhesion from './pages/ReglementAdhesion';
import VoirAdhesion from './pages/VoirAdhesion';
import VoirService from './pages/VoirService';
import AdminDashboard from './pages/AdminDashboard';
import ParametresAdhesion from './pages/ParametresAdhesion';
import Actualites from './pages/Actualites';
import Formations from './pages/Formations';
import Miellerie from './pages/Miellerie';
import GestionMiellerie from './pages/GestionMiellerie';
import QuiSommesNous from './pages/QuiSommesNous';
import MentionsLegales from './pages/MentionsLegales';
import CGU from './pages/CGU';
import CGV from './pages/CGV';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import Contact from './pages/Contact';
import BlogArticle from './pages/BlogArticle';
import ServiceMiellerie from './pages/ServiceMiellerie';
import ServiceUNAF from './pages/ServiceUNAF';
import ModifierUNAF from './pages/ModifierUNAF';
// import RegularisationRuches from './pages/RegularisationRuches';
// import VoirRegularisation from './pages/VoirRegularisation';
import AdminBlog from './pages/AdminBlog';
import AdminBlogForm from './pages/AdminBlogForm';
import Parametres from './pages/Parametres';
import AdminCommunications from './pages/AdminCommunications';
import AdminCommunicationForm from './pages/AdminCommunicationForm';
import AdminCommunicationDetail from './pages/AdminCommunicationDetail';
import SuperAdminOrganismes from './pages/SuperAdminOrganismes';
import SuperAdminManageAdmins from './pages/SuperAdminManageAdmins';
import SuperAdminDevTools from './pages/SuperAdminDevTools';
import SuperAdminPurges from './pages/SuperAdminPurges';
import SuperAdminAllUsers from './pages/SuperAdminAllUsers';
import SuperAdminCampagneDocuments from './pages/SuperAdminCampagneDocuments';
import AcceptationDocuments from './pages/AcceptationDocuments';
import DocumentsGate from './components/DocumentsGate';
import CompositionConseil from './pages/CompositionConseilNew';
import ModifierConseil from './pages/ModifierConseil';
import ModifierBureau from './pages/ModifierBureau';
import ModifierSecondCollege from './pages/ModifierSecondCollege';
import ModifierStagiaires from './pages/ModifierStagiaires';
import ModifierCompositionADAR from './pages/ModifierCompositionADAR';
import ModifierCompositionAMAIR from './pages/ModifierCompositionAMAIR';
import ModifierCompositionSAR from './pages/ModifierCompositionSAR';
import TraiterPeriodesExpirees from './pages/TraiterPeriodesExpirees';
import AdminPayments from './pages/AdminPayments';
import AdminSuiviAssurance from './pages/AdminSuiviAssurance';
import AdminStripeAccount from './pages/AdminStripeAccount';
import AdminReunions from './pages/AdminReunions';
import ReunionDetail from './pages/ReunionDetail';
import ReunionUserDetail from './pages/ReunionUserDetail';
import Users from './pages/Users';
import ImportAdhesions from './pages/ImportAdhesions';
import AdminEvents from './pages/AdminEvents';
import CreateEvent from './pages/CreateEvent';
import EventPage from './pages/EventPage';
import EventsCalendar from './pages/EventsCalendar';
// import AdminRegularisations from './pages/AdminRegularisations';
import { useAuth } from './context/AuthContext';

// Composant de protection des routes pour les utilisateurs connectés
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">
      <div className="text-lg text-gray-600">Chargement...</div>
    </div>;
  }
  
  if (!user) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const loginUrl = `/login?redirect=${encodeURIComponent(returnTo)}`;
    return <Navigate to={loginUrl} state={{ from: returnTo }} replace />;
  }
  
  return children;
};

// Composant de protection des routes pour les non-admins
// Note: Les admins avec double rôle (admin + user) peuvent accéder aux routes user
const NonAdminRoute = ({ children }) => {
  const { user } = useAuth();
  
  // Si l'utilisateur est admin mais n'a PAS le rôle user, rediriger vers l'interface admin
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    const userRoles = user.roles || [user.role];
    if (!userRoles.includes('user')) {
      return <Navigate to="/admin/dashboard" />;
    }
  }
  
  return children;
};

// Composant de protection des routes admin
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/" />;
  }
  
  // Vérifier si l'utilisateur a le rôle admin ou super_admin
  const userRoles = user.roles || [user.role];
  const hasAdminAccess = userRoles.includes('admin') || userRoles.includes('super_admin');
  
  if (!hasAdminAccess) {
    return <Navigate to="/" />;
  }
  
  return children;
};

// Composant de protection des routes super-admin
const SuperAdminRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/" />;
  }
  
  // Vérifier si l'utilisateur a le rôle super_admin
  const userRoles = user.roles || [user.role];
  if (!userRoles.includes('super_admin')) {
    return <Navigate to="/" />;
  }
  
  return children;
};

function App() {
  return (
    <Router>
      <PreprodBanner />
      <MaintenanceGate>
      <AuthProvider>
        <DocumentsGate>
        <ViewProvider>
          <OrganismeProvider>
            <ScrollToTop />
            <Layout>
              <Routes>
            <Route path="/acceptation-documents" element={
              <ProtectedRoute>
                <AcceptationDocuments />
              </ProtectedRoute>
            } />
            <Route path="/" element={<NewHome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/actualites" element={<Actualites />} />
            <Route path="/actualites/:slug" element={<BlogArticle />} />
            <Route path="/formations" element={<Formations />} />
            <Route path="/miellerie" element={<Miellerie />} />
            <Route path="/gestion-miellerie" element={
              <ProtectedRoute>
                <GestionMiellerie />
              </ProtectedRoute>
            } />
            <Route path="/qui-sommes-nous" element={<QuiSommesNous />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/cgu" element={<CGU />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <Dashboard />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/mes-adhesions" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <MesAdhesions />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/profil" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <EditProfile />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/securite" element={
              <ProtectedRoute>
                <Security />
              </ProtectedRoute>
            } />
            <Route path="/parametres" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <Parametres />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/adhesion" element={
              <NonAdminRoute>
                <Adhesion />
              </NonAdminRoute>
            } />
              <Route path="/groupement/sar" element={<GroupementSAR />} />
              <Route path="/groupement/amair" element={<GroupementAMAIR />} />
              <Route path="/groupement/adar" element={<GroupementADAR />} />
              <Route path="/groupement/coopemiel" element={<GroupementCOOPEMIEL />} />
            <Route path="/adhesion/sar" element={
              <NonAdminRoute>
                <AdhesionSAR />
              </NonAdminRoute>
            } />
            <Route path="/adhesion/amair" element={
              <NonAdminRoute>
                <AdhesionAMAIR />
              </NonAdminRoute>
            } />
            <Route path="/adhesion/adar" element={
              <NonAdminRoute>
                <AdhesionADAR />
              </NonAdminRoute>
            } />
            <Route path="/adhesion/coopemiel" element={
              <NonAdminRoute>
                <AdhesionCOOPEMIEL />
              </NonAdminRoute>
            } />
            <Route path="/adhesion/:id" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <VoirAdhesion />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/service/:id" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <VoirService />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/service/:id/modifier-unaf" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <ModifierUNAF />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            {/* <Route path="/regularisation-ruches" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <RegularisationRuches />
                </NonAdminRoute>
              </ProtectedRoute>
            } /> */}
            {/* <Route path="/regularisation/:id" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <VoirRegularisation />
                </NonAdminRoute>
              </ProtectedRoute>
            } /> */}
            <Route path="/admin/dashboard" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/parametres-adhesion" element={
              <AdminRoute>
                <ParametresAdhesion />
              </AdminRoute>
            } />
            <Route path="/admin/events" element={
              <AdminRoute>
                <AdminEvents />
              </AdminRoute>
            } />
            <Route path="/admin/events/new" element={
              <AdminRoute>
                <CreateEvent />
              </AdminRoute>
            } />
            <Route path="/admin/events/:id" element={
              <AdminRoute>
                <EventPage />
              </AdminRoute>
            } />
            <Route path="/admin/events/:id/edit" element={
              <AdminRoute>
                <CreateEvent />
              </AdminRoute>
            } />
            <Route path="/events" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <EventsCalendar />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/events/:id" element={
              <ProtectedRoute>
                <EventPage />
              </ProtectedRoute>
            } />
            <Route path="/events/:id/edit" element={
              <ProtectedRoute>
                <CreateEvent />
              </ProtectedRoute>
            } />
            <Route path="/reunions/:id" element={
              <ProtectedRoute>
                <ReunionUserDetail />
              </ProtectedRoute>
            } />
            <Route path="/adherents/:id" element={
              <AdminRoute>
                <AdhesionDetail />
              </AdminRoute>
            } />
            <Route path="/reglement-adhesion/:adhesionId" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <ReglementAdhesion />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/service/miellerie" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <ServiceMiellerie />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/service/assurance-unaf" element={
              <ProtectedRoute>
                <NonAdminRoute>
                  <ServiceUNAF />
                </NonAdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/actualites" element={
              <AdminRoute>
                <AdminBlog />
              </AdminRoute>
            } />
            <Route path="/admin/actualites/nouveau" element={
              <AdminRoute>
                <AdminBlogForm />
              </AdminRoute>
            } />
            <Route path="/admin/actualites/edit/:id" element={
              <AdminRoute>
                <AdminBlogForm />
              </AdminRoute>
            } />
            <Route path="/admin/communications" element={
              <AdminRoute>
                <AdminCommunications />
              </AdminRoute>
            } />
            <Route path="/admin/communications/nouveau" element={
              <AdminRoute>
                <AdminCommunicationForm />
              </AdminRoute>
            } />
            <Route path="/admin/communications/edit/:id" element={
              <AdminRoute>
                <AdminCommunicationForm />
              </AdminRoute>
            } />
            <Route path="/admin/communications/:id" element={
              <AdminRoute>
                <AdminCommunicationDetail />
              </AdminRoute>
            } />
            <Route path="/admin/composition-conseil" element={
              <AdminRoute>
                <CompositionConseil />
              </AdminRoute>
            } />
            <Route path="/admin/composition-conseil/modifier" element={
              <AdminRoute>
                <ModifierConseil />
              </AdminRoute>
            } />
            <Route path="/admin/composition-adar/modifier" element={
              <AdminRoute>
                <ModifierCompositionADAR />
              </AdminRoute>
            } />
            <Route path="/admin/composition-amair/modifier" element={
              <AdminRoute>
                <ModifierCompositionAMAIR />
              </AdminRoute>
            } />
            <Route path="/admin/composition-sar/modifier" element={
              <AdminRoute>
                <ModifierCompositionSAR />
              </AdminRoute>
            } />
            <Route path="/admin/traiter-periodes-expirees" element={
              <AdminRoute>
                <TraiterPeriodesExpirees />
              </AdminRoute>
            } />
            <Route path="/admin/composition-bureau/modifier" element={
              <AdminRoute>
                <ModifierBureau />
              </AdminRoute>
            } />
            <Route path="/admin/composition-second-college/modifier" element={
              <AdminRoute>
                <ModifierSecondCollege />
              </AdminRoute>
            } />
            <Route path="/admin/composition-stagiaires/modifier" element={
              <AdminRoute>
                <ModifierStagiaires />
              </AdminRoute>
            } />
            <Route path="/admin/payments" element={
              <AdminRoute>
                <AdminPayments />
              </AdminRoute>
            } />
            <Route path="/admin/suivi-assurance" element={
              <AdminRoute>
                <AdminSuiviAssurance />
              </AdminRoute>
            } />
            {/* <Route path="/admin/regularisations" element={
              <AdminRoute>
                <AdminRegularisations />
              </AdminRoute>
            } /> */}
            <Route path="/admin/stripe-account" element={
              <AdminRoute>
                <AdminStripeAccount />
              </AdminRoute>
            } />
            <Route path="/admin/reunions" element={
              <AdminRoute>
                <AdminReunions />
              </AdminRoute>
            } />
            <Route path="/admin/reunions/:id" element={
              <AdminRoute>
                <ReunionDetail />
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <Users />
              </AdminRoute>
            } />
            
            {/* Routes Super Admin */}
            <Route path="/super-admin/organismes" element={
              <SuperAdminRoute>
                <SuperAdminOrganismes />
              </SuperAdminRoute>
            } />
            <Route path="/super-admin/admins" element={
              <SuperAdminRoute>
                <SuperAdminManageAdmins />
              </SuperAdminRoute>
            } />
            <Route path="/super-admin/dev-tools" element={
              <SuperAdminRoute>
                <SuperAdminDevTools />
              </SuperAdminRoute>
            } />
            {process.env.NODE_ENV !== 'production' && (
              <Route path="/super-admin/purges" element={
                <SuperAdminRoute>
                  <SuperAdminPurges />
                </SuperAdminRoute>
              } />
            )}
            <Route path="/super-admin/import-adhesions" element={
              <SuperAdminRoute>
                <ImportAdhesions />
              </SuperAdminRoute>
            } />
            <Route path="/super-admin/all-users" element={
              <SuperAdminRoute>
                <SuperAdminAllUsers />
              </SuperAdminRoute>
            } />
            <Route path="/super-admin/campagne-documents" element={
              <SuperAdminRoute>
                <SuperAdminCampagneDocuments />
              </SuperAdminRoute>
            } />
          </Routes>
        </Layout>
          </OrganismeProvider>
        </ViewProvider>
        </DocumentsGate>
      </AuthProvider>
      </MaintenanceGate>
      <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </Router>
  );
}

export default App;
